import { useState, useEffect, useRef, useCallback } from "react";
import {
  Wind, Thermometer, Eye, Gauge, Cloud, RefreshCw,
  ExternalLink, AlertTriangle, Plane, ChevronDown, ChevronUp,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Configuração do aeródromo (futuramente virá do perfil da escola) ──────────
const SCHOOL_CONFIG = {
  icao: "LPBR",
  name: "Aeródromo de Braga",
  lat: 41.5871,
  lon: -8.4456,
  nearest_icao: "LPPR",
  nearest_name: "Porto (LPPR)",
  runways: [
    { id: "07", hdg: 70  },
    { id: "25", hdg: 250 },
  ],
  calm_default: "25",   // pista preferencial com vento indiferente (LPBR SOP)
  windy_cam_id: "1564818425",
  windy_cam_label: "Palmeira › North-east: Braga Airfield",
};

// ── WMO Weather Codes ────────────────────────────────────────────────────────
const WX = {
  0:  { pt: "Céu limpo",              cat: "VFR"  },
  1:  { pt: "Maioritariamente limpo", cat: "VFR"  },
  2:  { pt: "Parcialmente nublado",   cat: "MVFR" },
  3:  { pt: "Nublado",                cat: "MVFR" },
  45: { pt: "Nevoeiro",               cat: "IFR"  },
  48: { pt: "Nevoeiro c/ gelo",       cat: "IFR"  },
  51: { pt: "Chuvisco leve",          cat: "MVFR" },
  61: { pt: "Chuva leve",             cat: "MVFR" },
  63: { pt: "Chuva moderada",         cat: "IFR"  },
  65: { pt: "Chuva forte",            cat: "IFR"  },
  80: { pt: "Aguaceiros",             cat: "MVFR" },
  95: { pt: "Trovoada",               cat: "IFR"  },
};
function wxPt(code)  { return WX[code]?.pt  ?? `WX ${code}`; }
function wxCat(code) { return WX[code]?.cat ?? "VFR"; }
function catCls(cat) {
  return cat === "VFR" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
    :    cat === "MVFR" ? "text-amber-400  border-amber-500/40  bg-amber-500/10"
    :                    "text-red-400    border-red-500/40    bg-red-500/10";
}
function wxCls(code) {
  const c = wxCat(code);
  return c === "VFR" ? "text-emerald-400" : c === "MVFR" ? "text-amber-400" : "text-red-400";
}

// ── QNH calculator (barometric reduction: surface pressure → MSL) ─────────────
/**
 * Reduces surface pressure to mean-sea-level (QNH) using the ICAO barometric formula.
 * @param {number} pSurf  Surface pressure in hPa
 * @param {number} tempC  Temperature at surface in °C
 * @param {number} elevM  Station elevation in metres
 * @returns {number} QNH in hPa (rounded)
 */
function calcQNH(pSurf, tempC, elevM) {
  if (pSurf == null) return null;
  const T_K = (tempC ?? 15) + 273.15;
  return Math.round(pSurf * Math.pow(1 - (0.0065 * elevM) / (T_K + 0.0065 * elevM), -5.257));
}

// ── Simulated METAR builder ───────────────────────────────────────────────────
// WMO code → METAR present weather token
const WX_TOKEN = {
  45: "FG", 48: "FZFG",
  51: "-DZ", 53: "DZ", 55: "+DZ",
  61: "-RA", 63: "RA",  65: "+RA",
  71: "-SN", 73: "SN",  75: "+SN",
  80: "SHRA", 81: "SHRA", 82: "+SHRA",
  95: "TS", 96: "TSRA", 99: "+TSRA",
};

/**
 * Build a METAR-format string from Open-Meteo current conditions.
 * LPBR elevation: ~183 m AMSL (used for QNH reduction).
 * Returns null if data is insufficient.
 */
function buildSimulatedMetar(cur) {
  if (!cur || cur.weather_code == null) return null;
  const LPBR_ELEV_M = 183;

  // Timestamp (now UTC)
  const now = new Date();
  const dd = String(now.getUTCDate()).padStart(2, "0");
  const hh = String(now.getUTCHours()).padStart(2, "0");
  const mn = String(now.getUTCMinutes()).padStart(2, "0");

  // Wind
  const wdirRaw = cur.wind_direction_10m;
  const wspdRaw = Math.round(cur.wind_speed_10m || 0);
  const wgstRaw = Math.round(cur.wind_gusts_10m || 0);
  let wind;
  if (wspdRaw < 3) {
    wind = "VRB02KT";
  } else {
    const wdir10 = Math.round(wdirRaw / 10) * 10;
    wind = `${String(wdir10).padStart(3, "0")}${String(wspdRaw).padStart(2, "0")}KT`;
    if (wgstRaw > wspdRaw + 4) wind += `G${String(wgstRaw).padStart(2, "0")}`;
  }

  // Visibility (metres → METAR field in metres, 9999 = 10 km+)
  const visMraw = cur.visibility ?? 10000;
  let visF;
  if (visMraw >= 10000) visF = "9999";
  else if (visMraw >= 5000) visF = String(Math.round(visMraw / 1000) * 1000).padStart(4, "0");
  else                      visF = String(Math.round(visMraw / 100) * 100).padStart(4, "0");

  // Present weather token (from WMO code)
  const wxTok = WX_TOKEN[cur.weather_code] ?? null;

  // Cloud layer from cover %
  const cover = cur.cloud_cover ?? 0;
  const tempC = cur.temperature_2m ?? 15;
  const rh    = cur.relative_humidity_2m ?? 70;
  const td    = Math.round(tempC - (100 - rh) / 5);
  // LCL (100 ft per °C T/Td spread × 4, minimum 500 ft)
  const lclFt = Math.max(500, Math.round((tempC - td) * 400 / 100) * 100);
  const lclF  = String(Math.round(lclFt / 100)).padStart(3, "0");
  let cloud;
  if (cover <=  5) cloud = "SKC";
  else if (cover <= 25) cloud = `FEW${lclF}`;
  else if (cover <= 50) cloud = `SCT${lclF}`;
  else if (cover <= 87) cloud = `BKN${lclF}`;
  else                  cloud = `OVC${lclF}`;

  // Temperature / Dew point
  const fmtT = (v) => v >= 0
    ? String(Math.round(v)).padStart(2, "0")
    : `M${String(Math.abs(Math.round(v))).padStart(2, "0")}`;

  // QNH: use pressure_msl (already reduced to MSL by Open-Meteo — no formula needed)
  const pSurf = cur.surface_pressure ?? 1013;
  const qnh = Math.round(cur.pressure_msl ?? pSurf);

  const parts = [
    `METAR LPBR ${dd}${hh}${mn}Z`,
    wind,
    visF,
    wxTok,
    cloud,
    `${fmtT(tempC)}/${fmtT(td)}`,
    `Q${String(qnh).padStart(4, "0")}`,
    "NOSIG",
  ].filter(Boolean);

  return parts.join(" ");
}

function fmtVis(m) {
  if (m == null) return "—";
  if (m >= 10000) return ">10 km";
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}
function fmtH(iso) { return iso ? iso.slice(11, 16) : "—"; }
function padDeg(d) { return d != null ? `${String(Math.round(d)).padStart(3, "0")}°` : "—"; }

// ── Pista preferencial ───────────────────────────────────────────────────────
function preferredRunway(windDeg, windKt, runways, calmDefault) {
  // Vento calmo (<3 kt) ou desconhecido → pista preferencial por defeito
  if (windKt == null || windKt < 3) return calmDefault ?? runways[0]?.id ?? null;
  if (windDeg == null) return calmDefault ?? null;
  let best = null, bestAngle = 361;
  for (const rwy of runways) {
    const diff = Math.abs(((windDeg - rwy.hdg + 180) % 360) - 180);
    if (diff < bestAngle) { bestAngle = diff; best = rwy.id; }
  }
  return best;
}

// ── Windy Webcam ─────────────────────────────────────────────────────────────
function WindyCam({ config }) {
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    const s = document.createElement("script");
    s.src = "https://webcams.windy.com/webcams/public/embed/v2/script/player.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);
  return (
    <div className="rounded-lg overflow-hidden border border-cockpit-border bg-slate-900/60">
      <div className="text-[10px] text-slate-500 px-2 py-1 border-b border-cockpit-border uppercase tracking-wide">
        Webcam ao vivo — {config.name}
      </div>
      <div className="p-2">
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a
          name="windy-webcam-timelapse-player"
          data-id={config.windy_cam_id}
          data-play="day"
          data-loop="0"
          data-auto-play="0"
          data-force-full-screen-on-overlay-play="0"
          data-interactive="1"
          href={`https://windy.com/webcams/${config.windy_cam_id}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lpbr-webcam-link"
          className="text-xs text-blue-400 hover:underline"
        >
          {config.windy_cam_label}
        </a>
      </div>
    </div>
  );
}

// ── METAR/TAF (via backend proxy) ────────────────────────────────────────────
function useMetarTaf(icao) {
  const [metar, setMetar]         = useState(null);
  const [metarMeta, setMetarMeta] = useState(null);  // _meta.stale info
  const [taf,   setTaf]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mr, tr] = await Promise.all([
        fetch(`${API}/api/weather/metar?icao=${icao}&hours=3`),
        fetch(`${API}/api/weather/taf?icao=${icao}`),
      ]);
      if (mr.ok) {
        const d = await mr.json();
        setMetar(d.data?.[0] ?? null);
        setMetarMeta(d._meta ?? null);   // null = fresh, non-null = stale
      }
      if (tr.ok) { const d = await tr.json(); setTaf(d.data?.[0] ?? null); }
      setFetchedAt(new Date());
    } catch (e) {
      console.warn("[FlightPlanning] METAR/TAF:", e?.message);
    } finally {
      setLoading(false);
    }
  }, [icao]);

  useEffect(() => { load(); }, [load]);
  return { metar, metarMeta, taf, loading, fetchedAt, refresh: load };
}

// ── METAR/TAF display ────────────────────────────────────────────────────────

/** Format a Unix-seconds timestamp to 'DD MMM HH:MM UTC' */
function fmtUnix(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts) * 1000);
  const day = String(d.getUTCDate()).padStart(2, "0");
  const mon = d.toLocaleString("en", { month: "short", timeZone: "UTC" });
  const hh  = String(d.getUTCHours()).padStart(2, "0");
  const mm  = String(d.getUTCMinutes()).padStart(2, "0");
  return `${day} ${mon} ${hh}:${mm} UTC`;
}

/** Format wind from a forecast period */
function fmtFcstWind(fc) {
  if (!fc.wspd && fc.wdir == null) return null;
  const dir = fc.wdir === "VRB" ? "VRB" : String(Number(fc.wdir)).padStart(3, "0") + "°";
  const spd = `${fc.wspd ?? 0} kt`;
  const gst = fc.wgst ? ` G${fc.wgst} kt` : "";
  return `${dir} / ${spd}${gst}`;
}

/** Format visibility from a forecast period (AWC API value in statute miles → ICAO metres/km) */
function fmtFcstVis(fc) {
  if (!fc.visib) return null;
  if (fc.visib === "6+") return ">10 km";
  const sm = Number(fc.visib);
  if (isNaN(sm)) return String(fc.visib);
  const m = sm * 1609.344;                          // SM → metres
  if (m >= 9999) return ">10 km";
  if (m >= 5000) return `${(m / 1000).toFixed(0)} km`;
  return `${Math.round(m / 100) * 100} m`;          // round to nearest 100 m like METAR
}

/** Format cloud layers from a forecast period */
function fmtFcstClouds(fc) {
  if (!fc.clouds?.length) return null;
  return fc.clouds
    .filter(c => c.cover && c.cover !== "NSC" && c.cover !== "NCD")
    .map(c => `${c.cover}${c.base != null ? ` ${Number(c.base).toLocaleString("pt-PT")} ft` : ""}`)
    .join(" · ") || (fc.clouds.some(c => c.cover === "NSC" || c.cover === "NCD") ? "NSC" : null);
}

/** Row for one TAF period label (FM/BECMG/TEMPO/initial) */
const PERIOD_CLS = {
  null:   "border-slate-700 bg-slate-800/30",
  FM:     "border-blue-500/30 bg-blue-500/5",
  BECMG:  "border-sky-500/30 bg-sky-500/5",
  TEMPO:  "border-amber-500/30 bg-amber-500/5",
  PROB:   "border-orange-500/30 bg-orange-500/5",
};
const PERIOD_LABEL = { FM: "FM", BECMG: "BECMG", TEMPO: "TEMPO", PROB: "PROB" };

function TafPeriodRow({ fc }) {
  const change = fc.fcstChange ?? null;
  const wind   = fmtFcstWind(fc);
  const vis    = fmtFcstVis(fc);
  const clouds = fmtFcstClouds(fc);
  const cls    = PERIOD_CLS[change] ?? PERIOD_CLS.FM;
  const label  = change
    ? `${PERIOD_LABEL[change] ?? change}${fc.probability ? ` ${fc.probability}%` : ""}`
    : "BASE";

  return (
    <div className={`rounded border ${cls} p-2 space-y-1`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-bold uppercase tracking-wide text-slate-300">{label}</span>
        <span className="text-[9px] text-slate-500 font-mono">
          {fmtUnix(fc.timeFrom)}
          {" → "}
          {/* BECMG: show transition window end (timeBec) not TAF validity end (timeTo) */}
          {fmtUnix((change === "BECMG" && fc.timeBec) ? fc.timeBec : fc.timeTo)}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono">
        {wind   && <span className="text-slate-200"><span className="text-slate-500">VNT </span>{wind}</span>}
        {vis    && <span className="text-slate-200"><span className="text-slate-500">VIS </span>{vis}</span>}
        {fc.wxString && <span className="text-amber-300"><span className="text-slate-500">WX  </span>{fc.wxString}</span>}
        {clouds && <span className="text-slate-200"><span className="text-slate-500">NUV </span>{clouds}</span>}
      </div>
    </div>
  );
}

function MetarTafPanel({ metar, metarMeta, taf, loading, nearestName }) {
  const [decodedMetar, setDecodedMetar] = useState(false);
  const [decodedTaf,   setDecodedTaf]   = useState(false);

  function cloudStr(clouds) {
    if (!clouds?.length) return "CAVOK / sem dados";
    return clouds.map(c => `${c.cover} ${Number(c.base).toLocaleString("pt-PT")} ft`).join(" · ");
  }

  return (
    <div className="space-y-2">
      {/* METAR */}
      <div className="rounded-lg border border-cockpit-border bg-slate-900/60">
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-cockpit-border">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 uppercase tracking-wide">METAR — {nearestName}</span>
            {/* Badge desatualizado — mostra quando os dados vêm de cache */}
            {metarMeta?.stale && (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-400 font-medium uppercase"
                data-testid="metar-stale-badge"
                title={`Dados obtidos há ${metarMeta.cache_age_label}`}
              >
                Desatualizado · {metarMeta.cache_age_label}
              </span>
            )}
          </div>
          {loading && <RefreshCw className="w-3 h-3 text-slate-500 animate-spin" />}
        </div>

        {/* RAW — visible whenever text is available, regardless of decoded state */}
        {metar?.rawOb ? (
          <div className="p-2.5 space-y-2">
            <code className="block text-[11px] text-emerald-300 font-mono bg-slate-800/60 rounded px-2 py-1.5 break-all leading-relaxed" data-testid="metar-raw">
              {metar.rawOb}
            </code>
            {/* Decoded — toggle, independent of RAW */}
            <button type="button" onClick={() => setDecodedMetar(v => !v)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white" data-testid="metar-decoded-toggle">
              {decodedMetar ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {decodedMetar ? "Ocultar" : "Mostrar"} versão descodificada
            </button>
            {decodedMetar && (
              <div className="grid grid-cols-2 gap-1.5 text-[11px]" data-testid="metar-decoded">
                <div className="bg-slate-800/40 rounded p-1.5 border border-cockpit-border">
                  <div className="text-[9px] text-slate-500 mb-0.5">Vento</div>
                  <div className="font-mono text-slate-200">
                    {metar.wdir != null ? `${padDeg(metar.wdir)} / ${metar.wspd} kt` : "VRB"}
                    {metar.wgst ? ` G${metar.wgst}` : ""}
                  </div>
                </div>
                <div className="bg-slate-800/40 rounded p-1.5 border border-cockpit-border">
                  <div className="text-[9px] text-slate-500 mb-0.5">QNH / Temp / Orv</div>
                  <div className="font-mono text-slate-200">{metar.altim} hPa · {metar.temp}°/{metar.dewp}°</div>
                </div>
                <div className="bg-slate-800/40 rounded p-1.5 border border-cockpit-border">
                  <div className="text-[9px] text-slate-500 mb-0.5">Visibilidade</div>
                  <div className="font-mono text-slate-200">{metar.visib === "6+" ? ">10 km" : `${metar.visib} SM`}</div>
                </div>
                <div className="bg-slate-800/40 rounded p-1.5 border border-cockpit-border">
                  <div className="text-[9px] text-slate-500 mb-0.5">Nuvens</div>
                  <div className="font-mono text-slate-200 text-[10px]">{cloudStr(metar.clouds)}</div>
                </div>
                {metar.fltCat && (
                  <div className={`col-span-2 rounded p-1.5 border font-bold text-center ${catCls(metar.fltCat)}`}>
                    {metar.fltCat}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : loading ? (
          <p className="px-3 py-2 text-xs text-slate-500">A carregar METAR…</p>
        ) : (
          <p className="px-3 py-2 text-xs text-slate-500">Sem dados METAR disponíveis para {nearestName}.</p>
        )}
      </div>

      {/* TAF */}
      <div className="rounded-lg border border-cockpit-border bg-slate-900/60">
        <div className="px-3 py-1.5 border-b border-cockpit-border">
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">TAF — {nearestName}</span>
        </div>
        {/* RAW — visible whenever text is available, regardless of decoded state */}
        {taf?.rawTAF ? (
          <div className="p-2.5 space-y-2">
            <code className="block text-[11px] text-sky-300 font-mono bg-slate-800/60 rounded px-2 py-1.5 break-all leading-relaxed whitespace-pre-wrap" data-testid="taf-raw">
              {taf.rawTAF}
            </code>
            <button type="button" onClick={() => setDecodedTaf(v => !v)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-white" data-testid="taf-decoded-toggle">
              {decodedTaf ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {decodedTaf ? "Ocultar" : "Mostrar"} versão descodificada
            </button>
            {decodedTaf && (
              <div className="space-y-1.5" data-testid="taf-decoded">
                <div className="text-[10px] text-slate-400 font-mono">
                  <span className="text-slate-500">Válido: </span>
                  <span data-testid="taf-valid-from">{fmtUnix(taf.validTimeFrom)}</span>
                  <span className="text-slate-600"> → </span>
                  <span data-testid="taf-valid-to">{fmtUnix(taf.validTimeTo)}</span>
                </div>
                {(taf.fcsts || []).map((fc, i) => (
                  <TafPeriodRow key={i} fc={fc} />
                ))}
              </div>
            )}
          </div>
        ) : loading ? (
          <p className="px-3 py-2 text-xs text-slate-500">A carregar TAF…</p>
        ) : (
          <p className="px-3 py-2 text-xs text-slate-500">Sem dados TAF disponíveis para {nearestName}.</p>
        )}
      </div>
    </div>
  );
}

// ── Open-Meteo weather (via backend proxy — shared cache, no per-IP rate limits) ─
function useWeather(lat, lon) {
  const [wx, setWx]     = useState(null);
  const [loading, setL] = useState(false);
  const [error, setErr] = useState(null);
  const [fetchedAt, setFA] = useState(null);

  const load = useCallback(async () => {
    setL(true); setErr(null);
    try {
      const r = await fetch(
        `${API}/api/weather/openmeteo?lat=${lat}&lon=${lon}`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.error) throw new Error(d.reason || "API error");
      setWx(d); setFA(new Date());
    } catch (e) { setErr(e.message); }
    finally { setL(false); }
  }, [lat, lon]);

  useEffect(() => { load(); }, [load]);
  return { wx, loading, error, fetchedAt, refresh: load };
}

// ── NOTAMs ────────────────────────────────────────────────────────────────────
const NOTAM_LINKS = [
  { label: "FPL Briefing NAV Portugal — NOTAMs oficiais", url: "https://fplbriefing.nav.pt/login",                    desc: "Serviço oficial de briefing AIM Portugal" },
  { label: "NotamInfo — LPBR",                            url: "https://www.notaminfo.com/?airport=LPBR",            desc: "Visualizador de NOTAMs para LPBR" },
  { label: "IPMA — Meteorologia Aeronáutica",             url: "https://www.ipma.pt/pt/aeronautica/taf.metar/",     desc: "TAF e METAR em Portugal — IPMA" },
];

// ── Componente principal ──────────────────────────────────────────────────────
export default function FlightPlanningPanel() {
  const [open, setOpen] = useState(false);
  const cfg = SCHOOL_CONFIG;

  const { wx, loading: wxLoad, error: wxErr, fetchedAt: wxAt, refresh: refreshWx } = useWeather(cfg.lat, cfg.lon);
  const { metar, metarMeta, taf, loading: mtLoad, fetchedAt: mtAt, refresh: refreshMt } = useMetarTaf(cfg.nearest_icao);

  const cur = wx?.current || {};
  const hourly = wx?.hourly || {};

  // Pista em uso + vento — usar SEMPRE o modelo local (Open-Meteo nas coords de LPBR),
  // não o METAR de LPPR (outra localização). LPPR é só para referência meteorológica.
  const windDeg = cur.wind_direction_10m;
  const windKt  = cur.wind_speed_10m;
  const rwy = preferredRunway(windDeg, windKt, cfg.runways, cfg.calm_default);
  // VFR cat: preferir fltCat do METAR de LPPR (estação certificada), fallback para modelo
  const vfrCat = metar?.fltCat ?? wxCat(cur.weather_code);

  // Previsão horária — máx 3 por linha, próximas 6h
  const nowHour = new Date().getUTCHours();
  const forecastHours = (hourly.time || [])
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => { const h = new Date(t).getUTCHours(); return h >= nowHour && h < nowHour + 7; })
    .slice(0, 6);

  function handleRefresh() { refreshWx(); refreshMt(); }
  const anyLoading = wxLoad || mtLoad;
  const fetchedLabel = (mtAt || wxAt)?.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="rounded-xl border border-cockpit-border bg-slate-900/40" data-testid="flight-planning-panel">
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors"
        data-testid="flight-planning-toggle"
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Plane className="w-4 h-4 text-sky-400 shrink-0" />
          <span className="text-sm font-semibold text-white">{cfg.name} ({cfg.icao})</span>
          {/* Pista em uso */}
          {rwy && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border border-orange-500/40 bg-orange-500/10 text-orange-300 shrink-0" data-testid="rwy-in-use">
              RWY {rwy}
            </span>
          )}
          {/* Categoria VFR/IFR */}
          {vfrCat && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${catCls(vfrCat)}`} data-testid="vfr-cat-badge">
              {vfrCat}
            </span>
          )}
          {/* Resumo tempo */}
          {cur.weather_code != null && (
            <span className={`text-xs ${wxCls(cur.weather_code)}`}>
              {wxPt(cur.weather_code)} · {windKt != null ? `${windKt.toFixed ? windKt.toFixed(0) : windKt} kt` : ""} {windDeg != null ? padDeg(windDeg) : ""}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />}
      </button>

      {open && (
        <div className="p-3 space-y-3 border-t border-cockpit-border" data-testid="flight-planning-content">
          {/* Webcam */}
          <WindyCam config={cfg} />

          {/* Condições actuais */}
          <div className="rounded-lg border border-cockpit-border bg-slate-900/60">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-cockpit-border">
              <span className="text-[10px] text-slate-500 uppercase tracking-wide">
                Condições — {cfg.name} (modelo meteorológico)
              </span>
              <div className="flex items-center gap-2">
                {fetchedLabel && <span className="text-[9px] text-slate-600">{fetchedLabel}</span>}
                <button onClick={handleRefresh} disabled={anyLoading} className="text-slate-500 hover:text-white" data-testid="wx-refresh-btn">
                  <RefreshCw className={`w-3 h-3 ${anyLoading ? "animate-spin" : ""}`} />
                </button>
              </div>
            </div>
            {wxErr && (
              <div className="px-3 py-2 text-xs text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> {wxErr}
              </div>
            )}
            {cur.weather_code != null && (
              <div className="px-3 py-2.5 space-y-2.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-semibold ${wxCls(cur.weather_code)}`} data-testid="wx-condition">
                    {wxPt(cur.weather_code)}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                  <div className="rounded border border-cockpit-border bg-slate-800/40 p-1.5">
                    <div className="flex items-center gap-1 text-[9px] text-slate-500 mb-1"><Wind className="w-2.5 h-2.5" /> Vento</div>
                    <div className="font-mono text-slate-200 font-semibold" data-testid="wx-wind">
                      {padDeg(cur.wind_direction_10m)} {cur.wind_speed_10m?.toFixed(0)} kt
                    </div>
                    {cur.wind_gusts_10m > (cur.wind_speed_10m || 0) + 5 && (
                      <div className="text-amber-400 text-[10px]">G{cur.wind_gusts_10m?.toFixed(0)} kt</div>
                    )}
                  </div>
                  <div className="rounded border border-cockpit-border bg-slate-800/40 p-1.5">
                    <div className="flex items-center gap-1 text-[9px] text-slate-500 mb-1"><Gauge className="w-2.5 h-2.5" /> QNH / Temp</div>
                    <div className="font-mono text-slate-200 font-semibold" data-testid="wx-qnh">
                      {Math.round(cur.pressure_msl ?? cur.surface_pressure ?? 1013)} hPa
                    </div>
                    <div className="text-slate-400 text-[10px]">{cur.temperature_2m?.toFixed(1)}°C · {cur.relative_humidity_2m}% HR</div>
                  </div>
                  <div className="rounded border border-cockpit-border bg-slate-800/40 p-1.5">
                    <div className="flex items-center gap-1 text-[9px] text-slate-500 mb-1"><Eye className="w-2.5 h-2.5" /> Vis / Nuv.</div>
                    <div className="font-mono text-slate-200 font-semibold" data-testid="wx-vis">{fmtVis(cur.visibility)}</div>
                    <div className="text-slate-400 text-[10px]"><Cloud className="w-2.5 h-2.5 inline mr-0.5" />{cur.cloud_cover}%</div>
                  </div>
                </div>

                {/* Previsão horária — máx 3 colunas, depois wrap */}
                {forecastHours.length > 0 && (
                  <div>
                    <div className="text-[9px] text-slate-600 uppercase tracking-wide mb-1">Previsão horária (UTC)</div>
                    <div className="grid grid-cols-3 gap-1">
                      {forecastHours.map(({ t, i }) => (
                        <div key={t} className="rounded border border-cockpit-border bg-slate-800/30 p-1 text-center">
                          <div className="text-[9px] text-slate-500 font-mono">{fmtH(t)}</div>
                          <div className={`text-[9px] leading-tight font-semibold ${wxCls(hourly.weather_code?.[i])}`}>
                            {wxPt(hourly.weather_code?.[i])}
                          </div>
                          <div className="font-mono text-[9px] text-slate-300">{padDeg(hourly.wind_direction_10m?.[i])}</div>
                          <div className="font-mono text-[9px] text-slate-200">{hourly.wind_speed_10m?.[i]?.toFixed(0)} kt</div>
                          <div className="text-[9px] text-slate-500">{fmtVis(hourly.visibility?.[i])}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {wxLoad && !cur.weather_code && (
              <div className="px-3 py-4 text-xs text-slate-500 text-center">A carregar meteorologia…</div>
            )}
          </div>

          {/* METAR Simulado LPBR — mostrado sempre em simultâneo com o METAR oficial de LPPR */}
          {(() => {
            const sim = buildSimulatedMetar(cur);
            if (!sim) return null;
            return (
              <div className="rounded-lg border-2 border-amber-500/60 bg-amber-500/5" data-testid="simulated-metar-block">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-500/40 bg-amber-500/10">
                  <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                  <div>
                    <span className="text-xs font-bold text-amber-300 uppercase tracking-wide">
                      METAR SIMULADO — NÃO OFICIAL
                    </span>
                    <p className="text-[10px] text-amber-400/70 leading-tight">
                      Gerado a partir de modelo meteorológico (Open-Meteo). LPBR não possui estação METAR certificada.
                      Não deve ser utilizado para tomada de decisões operacionais.
                    </p>
                  </div>
                </div>
                <div className="px-3 py-2.5">
                  <code className="block text-[12px] text-amber-200 font-mono bg-slate-900/60 rounded px-2 py-1.5 break-all leading-relaxed" data-testid="simulated-metar-raw">
                    {sim}
                  </code>
                </div>
              </div>
            );
          })()}

          {/* METAR / TAF oficial — estação LPPR */}
          <div>
            <div className="text-[10px] text-slate-400 mb-1.5 uppercase tracking-wide flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 font-bold text-[9px]">OFICIAL</span>
              Estação mais próxima: {cfg.nearest_name}
            </div>
            <MetarTafPanel
              metar={metar}
              metarMeta={metarMeta}
              taf={taf}
              loading={mtLoad}
              nearestName={cfg.nearest_name}
            />
          </div>

          {/* NOTAMs */}
          <div className="rounded-lg border border-cockpit-border bg-slate-900/60">
            <div className="px-3 py-1.5 border-b border-cockpit-border">
              <span className="text-[10px] text-slate-500 uppercase tracking-wide">NOTAMs & Briefing</span>
            </div>
            <div className="p-2.5 space-y-1.5">
              {NOTAM_LINKS.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-between gap-2 px-2.5 py-2 rounded border border-cockpit-border bg-slate-800/40 hover:bg-slate-800 hover:border-blue-500/40 transition-colors group">
                  <div>
                    <div className="text-xs text-slate-200 group-hover:text-blue-300 font-medium">{l.label}</div>
                    <div className="text-[10px] text-slate-500">{l.desc}</div>
                  </div>
                  <ExternalLink className="w-3 h-3 text-slate-600 group-hover:text-blue-400 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
